import { z } from 'zod';
import { optionalText, paginationQuery, requiredText, email, password } from './common';
import { FEATURE_KEYS, LIMIT_KEYS } from '../plans';

export const checkoutPlanSchema = z.object({
  planCode: z.string().min(2).max(40),
  billingCycle: z.enum(['monthly', 'yearly']),
  couponCode: optionalText(40),
  /** CPF/CNPJ do pagador (exigido por gateways brasileiros). */
  document: optionalText(20),
});
export type CheckoutPlanInput = z.infer<typeof checkoutPlanSchema>;

export const cancelSubscriptionSchema = z.object({
  reason: optionalText(500),
  feedback: optionalText(2000),
});

// ---------------------------------------------------------------- Superadmin
const featureMapSchema = z.record(z.string(), z.boolean()).refine(
  (m) => Object.keys(m).every((k) => (FEATURE_KEYS as string[]).includes(k)),
  'Recurso desconhecido',
);
const limitMapSchema = z.record(z.string(), z.number().int().min(0).nullable()).refine(
  (m) => Object.keys(m).every((k) => (LIMIT_KEYS as string[]).includes(k)),
  'Limite desconhecido',
);

export const planInputSchema = z.object({
  code: z.string().trim().toLowerCase().regex(/^[a-z0-9_-]{2,40}$/, 'Código inválido'),
  name: requiredText(60),
  description: optionalText(300),
  highlight: z.boolean().default(false),
  sortOrder: z.number().int().min(0).max(1000).default(0),
  isPublic: z.boolean().default(true),
  isActive: z.boolean().default(true),
  priceMonthlyCents: z.number().int().min(0).max(100_000_000),
  priceYearlyCents: z.number().int().min(0).max(1_000_000_000),
  trialDays: z.number().int().min(0).max(90).default(0),
  features: featureMapSchema,
  limits: limitMapSchema,
});
export type PlanInput = z.infer<typeof planInputSchema>;

export const overridesSchema = z.object({
  features: featureMapSchema.optional(),
  limits: limitMapSchema.optional(),
});

export const adminLoginSchema = z.object({ email, password: z.string().min(1).max(128) });
export const adminMfaSchema = z.object({ code: z.string().trim().min(6).max(20) });

export const adminUserInputSchema = z.object({
  email,
  name: requiredText(120),
  role: z.enum(['owner', 'admin', 'support', 'finance', 'viewer']),
  password: password.optional(),
  allowedIps: z.array(z.string().trim().max(64)).max(20).default([]),
});

export const tenantListQuery = paginationQuery.extend({
  q: z.string().trim().max(100).optional(),
  status: z.enum(['active', 'suspended', 'canceled']).optional(),
  subscriptionStatus: z.enum(['trialing', 'active', 'past_due', 'canceled', 'expired']).optional(),
  planCode: z.string().max(40).optional(),
  sort: z.enum(['recent', 'oldest', 'name', 'revenue']).default('recent'),
});

export const tenantSuspendSchema = z.object({ reason: requiredText(300, 'Informe o motivo') });

export const tenantChangePlanSchema = z.object({
  planCode: z.string().min(2).max(40),
  billingCycle: z.enum(['monthly', 'yearly']).default('monthly'),
  status: z.enum(['trialing', 'active']).default('active'),
  periodDays: z.number().int().min(1).max(3660).optional(),
  priceCents: z.number().int().min(0).max(1_000_000_000).optional(),
  reason: requiredText(300, 'Informe o motivo'),
});

export const extendTrialSchema = z.object({
  days: z.number().int().min(1).max(90),
  reason: requiredText(300, 'Informe o motivo'),
});

export const impersonationStartSchema = z.object({
  reason: requiredText(300, 'Informe o motivo do acesso'),
  userId: z.string().uuid().optional(),
});

export const adminNoteSchema = z.object({ body: requiredText(4000) });

export const billingCouponInputSchema = z.object({
  code: z.string().trim().toUpperCase().regex(/^[A-Z0-9_-]{3,30}$/, 'Código inválido'),
  description: optionalText(200),
  percentOff: z.number().int().min(1).max(100),
  durationCycles: z.number().int().min(1).max(120).nullish(),
  maxRedemptions: z.number().int().min(1).max(1_000_000).nullish(),
  planCodes: z.array(z.string().max(40)).max(20).default([]),
  validUntil: z.string().datetime().nullish(),
  isActive: z.boolean().default(true),
});

export const announcementInputSchema = z.object({
  title: requiredText(120),
  body: requiredText(2000),
  level: z.enum(['info', 'success', 'warning', 'critical']).default('info'),
  planCodes: z.array(z.string().max(40)).max(20).default([]),
  link: optionalText(500),
  startsAt: z.string().datetime().optional(),
  endsAt: z.string().datetime().nullish(),
  isActive: z.boolean().default(true),
});

export const featureFlagInputSchema = z.object({
  key: z.string().regex(/^[a-z0-9_.-]{2,60}$/),
  description: optionalText(300),
  enabled: z.boolean(),
  companyIds: z.array(z.string().uuid()).max(1000).default([]),
});

export const auditQuery = paginationQuery.extend({
  companyId: z.string().uuid().optional(),
  actorId: z.string().max(64).optional(),
  actorType: z.enum(['user', 'admin', 'system', 'public', 'webhook']).optional(),
  action: z.string().max(80).optional(),
  entityType: z.string().max(40).optional(),
  entityId: z.string().max(64).optional(),
  severity: z.enum(['info', 'notice', 'warning', 'critical']).optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  q: z.string().trim().max(100).optional(),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type AuditQuery = z.infer<typeof auditQuery>;

export const markInvoicePaidSchema = z.object({
  paymentMethod: optionalText(40),
  notes: optionalText(300),
});

export const systemSettingsSchema = z.object({
  signupEnabled: z.boolean(),
  maintenanceMode: z.boolean(),
  maintenanceMessage: optionalText(300),
  supportWhatsapp: optionalText(30),
  supportEmail: optionalText(254),
  aiEnabled: z.boolean(),
  trialPlanCode: z.string().max(40),
  trialDays: z.number().int().min(0).max(90),
}).partial();
